import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Drawer, Input, Popconfirm, Space, Tag, Typography } from 'antd';
import {
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  PaperClipOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TOKEN_NAME } from '../../constants';
import { approveAssistantAction, conversationInput, streamAssistant } from './api';
import { canUseAssistant, pageContext } from './context';
import './PlannerAssistant.scss';

const baseUrl = process.env.REACT_APP_ASSISTANT_URL;
const demoMode = process.env.REACT_APP_DEMO_MODE === 'true';
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;

function readPdfBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.readAsDataURL(file);
  });
}

export function AssistantPanel({ endpoint, token }) {
  const { t, i18n } = useTranslation('assistant');
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [tools, setTools] = useState([]);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [notice, setNotice] = useState(null);
  // Proposed actions awaiting the operator's explicit approval (server-signed).
  const [approvals, setApprovals] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const fileInput = useRef(null);
  const request = useRef(null);
  const generation = useRef(0);
  const list = useRef(null);
  const inputRef = useRef(null);
  const context = pageContext(location, i18n.language, Cookies.get('date'));

  useEffect(
    () => () => {
      generation.current++;
      request.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (open && list.current) list.current.scrollTop = list.current.scrollHeight;
  }, [messages, tools, open]);

  const cancel = () => request.current?.abort();
  const clear = () => {
    generation.current++;
    cancel();
    request.current = null;
    setBusy(false);
    setMessages([]);
    setTools([]);
    setError(null);
    setUsage(null);
    setNotice(null);
    setApprovals([]);
    setAttachment(null);
    setInput('');
  };

  async function pickAttachment(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError(null);
      setNotice({ kind: 'error', text: t('attachInvalid') });
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setNotice({ kind: 'error', text: t('attachTooBig', { mb: Math.round(MAX_ATTACHMENT_BYTES / 1048576) }) });
      return;
    }
    try {
      const dataBase64 = await readPdfBase64(file);
      setAttachment({ name: file.name, mimeType: 'application/pdf', dataBase64 });
      setNotice(null);
    } catch {
      setNotice({ kind: 'error', text: t('attachInvalid') });
    }
  }

  async function approveAction(approval) {
    const current = generation.current;
    setApprovals((previous) =>
      previous.map((entry) => (entry.token === approval.token ? { ...entry, status: 'approving' } : entry)),
    );
    try {
      const outcome = await approveAssistantAction({ baseUrl: endpoint, token, approval: approval.token });
      if (generation.current !== current) return;
      setApprovals((previous) =>
        previous.map((entry) => (entry.token === approval.token ? { ...entry, status: 'executed' } : entry)),
      );
      // Recorded as a completed assistant turn so the next question knows it ran.
      setMessages((previous) => [
        ...previous,
        {
          id: 'exec-' + Date.now(),
          role: 'assistant',
          status: 'complete',
          content:
            t('executedMessage', { tool: t('tools.' + approval.tool, { defaultValue: approval.tool }) }) +
            '\n' +
            JSON.stringify(outcome.result).slice(0, 1500),
        },
      ]);
    } catch (failure) {
      if (generation.current !== current) return;
      setApprovals((previous) =>
        previous.map((entry) =>
          entry.token === approval.token
            ? { ...entry, status: 'failed', errorCode: failure.code || 'assistant_unavailable' }
            : entry,
        ),
      );
    }
  }

  async function send(value = input) {
    const question = value.trim();
    if (!question || question.length > 4000 || request.current) return;
    const current = ++generation.current;
    const controller = new AbortController();
    request.current = controller;
    const userId = current + '-user';
    const assistantId = current + '-assistant';
    const history = conversationInput(messages, question);
    setMessages((previous) => [
      ...previous,
      { id: userId, role: 'user', content: question, status: 'pending' },
      { id: assistantId, role: 'assistant', content: '', status: 'pending' },
    ]);
    const outgoingAttachment = attachment;
    setInput('');
    setError(null);
    setUsage(null);
    setNotice(null);
    setTools([]);
    setBusy(true);
    try {
      const result = await streamAssistant({
        baseUrl: endpoint,
        token,
        messages: history,
        context,
        attachment: outgoingAttachment || undefined,
        signal: controller.signal,
        onEvent: (event) => {
          if (generation.current !== current) return;
          if (event.type === 'delta' && typeof event.text === 'string') {
            setMessages((previous) =>
              previous.map((message) =>
                message.id === assistantId ? { ...message, content: message.content + event.text } : message,
              ),
            );
          } else if (event.type === 'approval') {
            setApprovals((previous) => [...previous, { ...event, status: 'pending' }]);
          } else if (event.type === 'notice' && event.code === 'step_limit') {
            setNotice({ kind: 'info', text: t('limitReached') });
          } else if (event.type === 'tool') {
            setTools((previous) =>
              event.phase === 'start'
                ? [...previous, { name: event.name, complete: false }]
                : previous.map((tool) => (tool.name === event.name ? { ...tool, complete: true } : tool)),
            );
          }
        },
      });
      if (generation.current !== current) return;
      setMessages((previous) =>
        previous.map((message) => {
          if (message.id === assistantId) return { ...message, content: result.text, status: 'complete' };
          if (message.id === userId) return { ...message, status: 'complete' };
          return message;
        }),
      );
      setUsage(result.usage);
      setAttachment(null);
    } catch (failure) {
      if (generation.current !== current) return;
      const cancelled = controller.signal.aborted;
      setMessages((previous) =>
        previous.map((message) =>
          [userId, assistantId].includes(message.id)
            ? { ...message, status: cancelled ? 'cancelled' : 'error' }
            : message,
        ),
      );
      setError(cancelled ? null : failure.code || 'assistant_unavailable');
      setInput(question);
    } finally {
      if (generation.current === current) {
        request.current = null;
        setBusy(false);
        inputRef.current?.focus();
      }
    }
  }

  const suggestions = context.orderCode
    ? [t('suggestOrder', { code: context.orderCode }), t('suggestHistory', { code: context.orderCode })]
    : context.routeId
      ? [t('suggestRoute', { id: context.routeId })]
      : [
          context.date ? t('suggestDate', { date: context.date }) : t('suggestToday'),
          t('suggestIntake'),
          t('suggestHelp'),
        ];
  const contextLabel = context.orderCode || (context.routeId ? '#' + context.routeId : context.date || context.page);

  return (
    <>
      <Button
        className="planner-assistant-trigger"
        type="primary"
        icon={<CommentOutlined />}
        onClick={() => setOpen(true)}
        aria-label={t('open')}
      >
        {t('title')}
      </Button>
      <Drawer
        title={
          <Space>
            <CommentOutlined />
            {t('title')}
            <Tag>{t('readOnly')}</Tag>
          </Space>
        }
        open={open}
        width={460}
        zIndex={10020}
        rootClassName="planner-assistant-drawer"
        onClose={() => {
          cancel();
          setOpen(false);
        }}
        closeIcon={<CloseOutlined aria-label={t('close')} />}
        extra={
          <Button type="text" icon={<DeleteOutlined />} aria-label={t('clear')} title={t('clear')} onClick={clear} />
        }
        afterOpenChange={(visible) => {
          if (visible) inputRef.current?.focus();
        }}
      >
        <div className="planner-assistant">
          <div className="planner-assistant-notice">
            <Typography.Text type="secondary">{t('privacy')}</Typography.Text>
            {contextLabel && (
              <div className="planner-assistant-context">
                {t('context')}: <strong>{contextLabel}</strong>
              </div>
            )}
          </div>
          <div
            className="planner-assistant-messages"
            ref={list}
            role="log"
            aria-label={t('conversation')}
            aria-live="polite"
            aria-busy={busy}
          >
            {messages.length === 0 && (
              <div className="planner-assistant-empty">
                <Typography.Title level={4}>{t('welcome')}</Typography.Title>
                <Typography.Paragraph type="secondary">{t('description')}</Typography.Paragraph>
                <Space direction="vertical" className="planner-assistant-suggestions">
                  {suggestions.map((suggestion) => (
                    <Button key={suggestion} onClick={() => send(suggestion)}>
                      {suggestion}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
            {messages.map((message) => (
              <article key={message.id} className={'planner-assistant-message ' + message.role}>
                <div className="planner-assistant-author">{message.role === 'user' ? t('you') : t('title')}</div>
                <div className="planner-assistant-text">
                  {message.content || (message.status === 'pending' ? t('thinking') : '')}
                </div>
                {['cancelled', 'error'].includes(message.status) && message.role === 'assistant' && (
                  <Typography.Text type="secondary">
                    {t(message.status === 'cancelled' ? 'stopped' : 'incomplete')}
                  </Typography.Text>
                )}
              </article>
            ))}
            {tools.length > 0 && (
              <div className="planner-assistant-tools">
                {tools.map((tool, index) => (
                  <Tag key={index} color={tool.complete ? 'green' : 'blue'}>
                    {t('tools.' + tool.name, { defaultValue: tool.name })} · {t(tool.complete ? 'checked' : 'checking')}
                  </Tag>
                ))}
              </div>
            )}
            {approvals.map((approval) => (
              <Card
                key={approval.token}
                size="small"
                className={'planner-assistant-approval ' + approval.status}
                title={
                  <Space>
                    <Tag color={approval.status === 'executed' ? 'green' : 'orange'}>
                      {t('tools.' + approval.tool, { defaultValue: approval.tool })}
                    </Tag>
                    {approval.status === 'executed' ? t('executedTitle') : t('approvalTitle')}
                  </Space>
                }
              >
                <Typography.Text type="secondary">{t('approvalHint')}</Typography.Text>
                <pre className="planner-assistant-approval-args">{JSON.stringify(approval.args, null, 1)}</pre>
                <details>
                  <summary>{t('approvalPreview')}</summary>
                  <pre className="planner-assistant-approval-preview">{approval.preview}</pre>
                </details>
                {approval.status === 'failed' && (
                  <Alert
                    showIcon
                    type="error"
                    message={t('errors.' + approval.errorCode, { defaultValue: t('errors.assistant_unavailable') })}
                  />
                )}
                {approval.status === 'dismissed' && (
                  <Typography.Text type="secondary">{t('dismissed')}</Typography.Text>
                )}
                {['pending', 'approving', 'failed'].includes(approval.status) && (
                  <Space className="planner-assistant-approval-actions">
                    <Popconfirm
                      title={t('approveConfirm')}
                      okText={t('approve')}
                      cancelText={t('dismiss')}
                      onConfirm={() => approveAction(approval)}
                      disabled={approval.status === 'approving'}
                    >
                      <Button type="primary" danger loading={approval.status === 'approving'}>
                        {approval.status === 'approving' ? t('approving') : t('approve')}
                      </Button>
                    </Popconfirm>
                    <Button
                      disabled={approval.status === 'approving'}
                      onClick={() =>
                        setApprovals((previous) =>
                          previous.map((entry) =>
                            entry.token === approval.token ? { ...entry, status: 'dismissed' } : entry,
                          ),
                        )
                      }
                    >
                      {t('dismiss')}
                    </Button>
                  </Space>
                )}
              </Card>
            ))}
          </div>
          {error && (
            <Alert
              showIcon
              type="error"
              message={t('errors.' + error, { defaultValue: t('errors.assistant_unavailable') })}
            />
          )}
          {notice && <Alert showIcon type={notice.kind === 'error' ? 'warning' : 'info'} message={notice.text} />}
          <form
            className="planner-assistant-composer"
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            <Input.TextArea
              ref={inputRef}
              aria-label={t('placeholder')}
              placeholder={t('placeholder')}
              value={input}
              maxLength={4000}
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={busy}
              onChange={(event) => setInput(event.target.value)}
              onPressEnter={(event) => {
                if (!event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  send();
                }
              }}
            />
            {attachment && (
              <div className="planner-assistant-attachment">
                <Tag
                  closable
                  onClose={() => setAttachment(null)}
                  closeIcon={<CloseOutlined aria-label={t('attachRemove')} />}
                >
                  <PaperClipOutlined /> {attachment.name}
                </Tag>
              </div>
            )}
            <div className="planner-assistant-actions">
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={pickAttachment}
                aria-label={t('attach')}
              />
              <Button
                type="text"
                icon={<PaperClipOutlined />}
                aria-label={t('attach')}
                title={t('attach')}
                disabled={busy}
                onClick={() => fileInput.current?.click()}
              />
              <Typography.Text type="secondary">
                {usage ? t('usage', { count: (usage.inputTokens || 0) + (usage.outputTokens || 0) }) : t('hint')}
              </Typography.Text>
              {busy ? (
                <Button icon={<StopOutlined />} onClick={cancel} aria-label={t('stop')}>
                  {t('stop')}
                </Button>
              ) : (
                <Button
                  htmlType="submit"
                  type="primary"
                  icon={<SendOutlined />}
                  disabled={!input.trim()}
                  aria-label={t('send')}
                >
                  {t('send')}
                </Button>
              )}
            </div>
          </form>
        </div>
      </Drawer>
    </>
  );
}

export default function PlannerAssistant() {
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const [token, setToken] = useState(() => Cookies.get(TOKEN_NAME));
  // Session changes (including impersonation/logout in another tab) must
  // discard the conversation and cancel any request, even on an idle page.
  useEffect(() => {
    if (!baseUrl || demoMode) return undefined;
    const refresh = () => setToken(Cookies.get(TOKEN_NAME));
    const timer = setInterval(refresh, 1000);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  if (!baseUrl || demoMode || !token || !canUseAssistant(auth) || !location.pathname.startsWith('/admin/')) return null;
  return <AssistantPanel key={auth.user.id + ':' + token} endpoint={baseUrl} token={token} />;
}
